// Dependency imports
import {
  CircularProgress,
  IconButton,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useMemo, useEffect } from 'react';

// Local imports
import { useListInferenceModels, useDeleteSequenceModel } from 'api/sdk';
import { ConfigurationCameraResponseSchema } from 'schemas';
import LinearProgressWithLabel from 'components/progress/linearProgress';
import { useConfirm } from 'plugins/confirm';
import { Modal } from 'components';
import { useSnack } from 'plugins/snack';
import Swal from 'sweetalert2';
import InfoPopover from './infoPopover';
import { useWorkflow } from 'hooks/config/useWorkflow';
import clsx from 'clsx';

// Style imports

interface DropdownListProps {
  record: ConfigurationCameraResponseSchema[];
  siteId: string;
  configId: string;
}

export const DropdownList = ({
  record,
  siteId,
  configId
}: DropdownListProps) => {
  const {
    data,
    isLoading: isLabelLoading,
    refetch
  } = useListInferenceModels(siteId, {
    config_id: configId
  });
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [refreshTimeout, setRefreshTimeout] = useState<any>();
  const [isEdit, setEdit] = useState(false);
  const [selectedModel, setSelectedModel] = useState<any[]>([]);
  const snack = useSnack();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<any>('');
  const { mutateAsync: deleteModel } = useDeleteSequenceModel();

  const ActionToolTip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black
    }
  }));

  useEffect(() => {
    if (data && data?.data?.length > 0) {
      data?.data.every((item, index) => {
        if (item.status === 'TRAINING' || item.status === 'QUEUED') {
          const timeoutId = setTimeout(() => {
            refetch();
          }, 5000);
          setRefreshTimeout(timeoutId);
          return false;
        }
      });
    }

    // Clear timeouts
    return () => clearTimeout(refreshTimeout);
  }, [data]);

  const styleContainerCell = {
    flex: 1,
    padding: '10px 0'
  };

  const styleHeaderCell = {
    flex: 1,
    padding: '10px 0',
    background: '#102633'
  };

  const breakStyle = {
    height: '40px',
    width: '1px',
    background: '#fff'
  };

  const handleOpen = (modelId: string) => {
    setSelectedModelId(modelId);
  };

  const handleClose = () => {
    setSelectedModelId(null);
    refetch();
  };

  const handleDeleteModel = async (id: string) => {
    Swal.fire({
      title: 'Delete Model!',
      text: 'Are you sure you want to delete this model?',
      allowOutsideClick: false,
      confirmButtonText: 'Yes, delete it!',
      showCancelButton: true,
      confirmButtonColor: '#E33030',
      cancelButtonColor: '#102633',
      cancelButtonText: 'No, cancel!',
      focusCancel: true,
      customClass: {
        title: 'swal2-title',
        container: 'swal2-html-container',
        actions: 'swal2-actions'
      }
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          await deleteModel({
            configId: configId,
            modelId: id
          });
          refetch();
          snack({
            message: 'Model deleted successfully',
            severity: 'success'
          });
        } catch (error: any) {
          console.log('error', error);
          snack({
            message:
              error?.response?.data?.detail ===
              'Cannot delete model due to existing dependencies. Constraint: inference_model'
                ? 'Cannot be deleted as the model is used for inference.'
                : 'Oops! something went wrong',
            severity: 'error'
          });
        }
      }
    });
  };

  return isLabelLoading ? (
    <div className="flex justify-center pt-[10px]">
      <CircularProgress className="w-[1vw]" />
    </div>
  ) : (
    <Stack direction={'column'}>
      {data?.data && data?.data.length === 0 ? (
        <p className="text-[1vw] flex justify-center items-center pt-[10px]">
          No Data Found !
        </p>
      ) : (
        <>
          {data?.data && data?.data?.length > 0 && (
            <Stack
              direction="row"
              gap={2}
              alignItems={'center'}
              justifyContent="space-between"
              sx={{ marginBottom: 1, background: '#102633' }}
            >
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Model Name</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Model Type</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[1vw] text-white">Created Date</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Trained Videos</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Annotations</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Learning Cycles</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Status</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Accuracy</p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={styleHeaderCell}
              >
                <p className="text-[1vw] text-white">Actions</p>
                <Stack />
              </Stack>
            </Stack>
          )}
          {data?.data.map((model, idx) => (
            <Stack
              key={model.id}
              direction="row"
              gap={2}
              alignItems={'center'}
              justifyContent="space-between"
              sx={{ marginBottom: 1, background: '#EEEEEE' }}
            >
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model.model_name}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model.model_type?.toUpperCase()}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {moment.utc(model?.created_at).local().format('lll')}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model?.trained_videos_count}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model?.annotationCount}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model?.current_epoch || 0}/{model?.total_epochs}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              {model?.status === 'TRAINING' ? (
                <ActionToolTip title={'Training'} style={{ cursor: 'pointer' }}>
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={styleContainerCell}
                  >
                    <div
                      style={{
                        width: '70%'
                      }}
                    >
                      <LinearProgressWithLabel
                        value={model?.progress as number}
                      />
                    </div>
                  </Stack>
                </ActionToolTip>
              ) : (
                <ActionToolTip
                  title={
                    model?.status === 'COMPLETED'
                      ? 'Completed'
                      : model?.status === 'QUEUED'
                      ? 'Queued'
                      : model?.status === 'FAILED'
                      ? model?.fail_reason
                      : 'Loading...'
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={styleContainerCell}
                  >
                    <p
                      className={clsx(
                        'text-[0.9vw] text-[#102633]',
                        model?.status === 'COMPLETED' && 'text-[#12AD2B]',
                        model?.status === 'QUEUED' && 'text-orange',
                        model?.status === 'FAILED' && 'text-red'
                      )}
                    >
                      {model?.status}
                    </p>
                  </Stack>
                </ActionToolTip>
              )}

              <div style={breakStyle}></div>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={styleContainerCell}
              >
                <p className="text-[0.8vw] text-[#102633]">
                  {model?.accuracy
                    ? `${(model?.accuracy * 100).toFixed(2)}%`
                    : '-'}
                </p>
              </Stack>
              <div style={breakStyle}></div>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={styleContainerCell}
              >
                <IconButton
                  onClick={() => {
                    handleOpen(model.id);
                    model?.workflow_configurations &&
                      setSelectedModel([model?.workflow_configurations[0]]);
                    model?.workflow_configurations &&
                      setSelectedWorkflowId(
                        model?.workflow_configurations[0]?.id
                      );
                  }}
                >
                  <InfoOutlinedIcon style={{ fill: 'black', width: '1.2vw' }} />
                </IconButton>
                <IconButton onClick={() => handleDeleteModel(model?.id)}>
                  <DeleteIcon style={{ fill: 'black', width: '1.2vw' }} />
                </IconButton>
              </Stack>
              <Modal
                open={selectedModelId === model.id}
                onClose={handleClose}
                size="xl"
              >
                <InfoPopover
                  configId={configId}
                  model={model as any}
                  isEdit={isEdit}
                  setEdit={setEdit}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  handleClose={handleClose}
                  selectedWorkflowId={selectedWorkflowId}
                  setSelectedWorkflowId={setSelectedWorkflowId}
                />
              </Modal>
            </Stack>
          ))}
        </>
      )}
    </Stack>
  );
};
